// Client-side in-memory cache for DossierPublic, keyed by cardId.
//
// Populated at match start by `prefetchDossiers(cardIds)` — fires the
// per-card fetches in parallel and stashes both successes and 404s
// (as null) so subsequent lookups don't re-hit the network for cards
// we already know are silent.
//
// Lookup is synchronous: `lookupDossier(cardId)` returns the cached
// entry (DossierPublic | null) or undefined if we haven't asked yet.
// The voice-line renderer treats undefined the same as null — stays
// silent until the prefetch resolves.

import type { DossierPublic } from '../shared/dossier-types.js';
import { apiClient } from '../api-client.js';

// `undefined` means "never fetched"; `null` means "fetched, no dossier".
const cache = new Map<string, DossierPublic | null>();

/** Server timestamp the last `refreshDossierCache()` saw. Sent as the
 *  cursor on the next call; null means "no cursor yet, just bootstrap". */
let lastUpdatedSinceTs: string | null = null;

/** Synchronous lookup. Returns the cached DossierPublic, null if the
 *  card has no dossier on the server, or undefined if we haven't
 *  asked yet (treat undefined the same as null at the call site). */
export function lookupDossier(cardId: string): DossierPublic | null | undefined {
  return cache.get(cardId);
}

/** Convenience for renderer code that doesn't care about the "never
 *  asked" distinction — returns null if either undefined or null. */
export function lookupDossierOrNull(cardId: string): DossierPublic | null {
  const hit = cache.get(cardId);
  return hit ?? null;
}

/** Issue parallel fetches for every cardId not already cached. Errors
 *  on individual fetches are caught and recorded as null so the cache
 *  has a result for every requested id when the promise settles. */
export async function prefetchDossiers(cardIds: Array<string | null | undefined>): Promise<void> {
  const wanted = new Set<string>();
  for (const id of cardIds) {
    if (typeof id !== 'string' || id.length === 0) continue;
    if (cache.has(id)) continue;
    wanted.add(id);
  }
  if (wanted.size === 0) return;

  await Promise.all(
    Array.from(wanted, async (id) => {
      try {
        const dossier = await apiClient.getDossierPublic(id);
        cache.set(id, dossier);
      } catch (e) {
        // Network / 5xx — record as null so we don't retry on every
        // bubble attempt. The console.warn helps surface real issues
        // without blocking the play experience.
        console.warn(`[dossier-cache] failed to fetch ${id}:`, e);
        cache.set(id, null);
      }
    }),
  );
}

/** Ask the server which dossiers became ready since the last refresh
 *  and evict those entries from the cache so the next `prefetchDossiers`
 *  re-fetches them. ONE HTTP call per invocation regardless of cache
 *  size — the server returns just the changed card IDs. Eviction is
 *  surgical: untouched entries (positive or null) remain cached, so
 *  the cache's hit rate stays high across long sessions.
 *
 *  Errors are swallowed: a transient network failure shouldn't block
 *  match start. The cursor is only advanced on success, so the next
 *  call will still pick up anything missed by the failed attempt. */
export async function refreshDossierCache(): Promise<void> {
  try {
    const resp = await apiClient.getDossiersUpdatedSince(lastUpdatedSinceTs);
    for (const cardId of resp.cardIds) {
      cache.delete(cardId);
    }
    lastUpdatedSinceTs = resp.ts;
  } catch (e) {
    console.warn('[dossier-cache] refresh failed:', e);
  }
}

/** Test / dev convenience — clear the cache so a refetch is forced.
 *  Not used in normal play. */
export function clearDossierCache(): void {
  cache.clear();
  lastUpdatedSinceTs = null;
}
